import React, { useState, useRef, useContext } from "react"
import { mutationRequest } from "../../util/request"
import Header from "../../components/shared/Header/Header"
import { RequestError } from "../../components/shared/RequestError"
import { Spinner } from "../../components/shared/Spinner"
import { OutlineButton } from "../../components/shared/Button"
import { DirectUpload } from "@rails/activestorage"
import Cropper from "react-cropper"
import "cropperjs/dist/cropper.css"
import { CardImage } from "react-bootstrap-icons"
import style from "../../components/shared/PageStyle.module.css"
import { BandEditForm } from "./BandEditForm"
import { BandDeleteForm } from "./BandDeleteForm"
import { useBand } from "../../hooks/useBand"
import { UserContext } from "../../components/HomeApp"
import Footer from "../../components/shared/Footer/Footer"
import { Band } from "../../types/bands"

interface BandResponse {
  band: Band & { cover_photo_url_large?: string }
}

interface UpdateBandResponse {
  success?: boolean
  band?: Band & { cover_photo_url_large?: string }
}

// OutlineButton's border color is intentionally fixed to gray for every
// other caller in the app, so a green variant can't just override it via
// className (same-specificity border-color utilities don't resolve by
// source order - see OutlineButton's own comment). This is the "good
// action" (upload/replace/save) outline button for the cover photo flow.
const GreenOutlineButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = ({ className = "", disabled, ...props }) => (
  <button
    type="button"
    disabled={disabled}
    className={`focus:outline-none border text-base border-green-700 text-green-700 px-2 py-1 rounded-sm align-bottom ${
      disabled ? "opacity-50" : ""
    } ${className}`}
    {...props}
  />
)

export const BandEditPage: React.FC = () => {
  const { user } = useContext(UserContext)
  const {
    status: bandStatus,
    data: bandData,
    error: bandError,
  } = useBand(user.band_id, "active", "backline")
  var band = (bandData as BandResponse)?.band

  return (
    <div className={style.rootElStyle}>
      <Header user={user} />
      {bandStatus === "error" ? (
        <RequestError error={bandError} />
      ) : bandStatus === "loading" || !band ? (
        <Spinner />
      ) : (
        <>
          <div className="grid grid-cols-12 pb-2 px-4">
            <div className="col-span-12 border-b-4 mt-4 lg:mb-8 mb-4 border-black">
              <h1 className="lg:col-span-8 col-span-12 font-extrabold lg:text-3xl text-xl uppercase">
                Manage Page for {band.name}
              </h1>
            </div>
            <div className="col-span-12">
              <div className="text-base">
                bandNada can pull in data from other band related services and
                then display them on your bandNada page. The more info you
                provide, the better your page will look, and the more likely
                someone new will pay attention to your page
              </div>
              <BandEditForm band={band} />
              <h2 className="lg:col-span-8 col-span-12 font-bold border-b mb-4 border-black lg:text-3xl text-xl uppercase lg:mt-10 mt-5">
                Delete Band Completely
              </h2>
              <p className="pb-4 text-base">
                This will remove all of your band related data stored with
                bandNada, including: followers, stream data, and post data.
                Payments and tips will continue to be stored for accounting
                purposes but not displayed publicly.
              </p>
              <BandDeleteForm band={band} />
            </div>
          </div>
        </>
      )}
      <Footer />
    </div>
  )
}

interface CoverPhotoUploadSectionProps {
  band: Band & { cover_photo_url_large?: string }
}

export const CoverPhotoUploadSection: React.FC<
  CoverPhotoUploadSectionProps
> = ({ band }) => {
  const [coverPhotoURL, setCoverPhotoURL] = useState<string>(
    band.cover_photo_url_large || "",
  )
  const [btnActive, setBtnActive] = useState<boolean>(true)
  const [cropper, setCropper] = useState<Cropper | null>(null)
  const [uploadedFilePath, setUploadedFilePath] = useState<string>("")
  const [uploadError, setUploadError] = useState<string | null>(null)

  const hiddenFileInput = useRef<HTMLInputElement>(null)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    hiddenFileInput.current?.click()
  }

  const reader = new FileReader()
  reader.onload = (e) => {
    setUploadedFilePath(e.target?.result as string)
  }

  const onUploadCoverPhoto = (the_attachment: File) => {
    setBtnActive(false)
    reader.readAsDataURL(the_attachment)
    setBtnActive(true)
  }

  const onRemovePhoto = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setBtnActive(false)
    mutationRequest({
      path: `/api/v1/bands/${band.id}/remove_cover_photo`,
      options: { method: "PUT" },
    }).then((response) => {
      setCoverPhotoURL("")
      setBtnActive(true)
    })
  }

  const onCancel = () => {
    setBtnActive(false)
    if (hiddenFileInput.current) {
      hiddenFileInput.current.value = ""
    }
    setUploadedFilePath("")
    setBtnActive(true)
  }

  const onSave = () => {
    setBtnActive(false)

    cropper?.getCroppedCanvas({ maxWidth: 1200, maxHeight: 1200 }).toBlob((blob) => {
      if (!blob) return

      const upload = new DirectUpload(
        new File([blob], "cropped-photo"),
        "/rails/active_storage/direct_uploads",
      )

      upload.create((error, blob) => {
        if (error) {
          console.error("error:", error)
          setUploadError("Upload failed. Please try again.")
          setBtnActive(true)
        } else {
          const signed_id = blob.signed_id
          const request_body = {
            cover_photo: signed_id,
          }
          handleUpdateBandData(band.id, request_body).then(
            (response: UpdateBandResponse | undefined) => {
              if (response?.band?.cover_photo_url_large) {
                setCoverPhotoURL(response.band.cover_photo_url_large)
                setUploadError(null)
              } else {
                setUploadError("Failed to save photo. Please try again.")
              }
              if (hiddenFileInput.current) {
                hiddenFileInput.current.value = ""
              }
              setUploadedFilePath("")
              setBtnActive(true)
            },
          )
        }
      })
    })
  }

  const hasCoverPhoto = !!(coverPhotoURL && coverPhotoURL.length)

  return (
    <div className="grid grid-cols-12 mt-10">
      <label className="block col-span-12 text-gray-900 text-xs uppercase font-bold">
        Cover Photo Image (Highly Recommended)
      </label>
      <p className="col-span-12 text-xs text-gray-500 mb-2">
        Tip: Landscape proportion looks best
      </p>
      <div className="col-span-12 lg:col-span-2">
        <div
          className="w-full overflow-hidden rounded-sm bg-gray-200"
          style={{ aspectRatio: "16 / 9" }}
        >
          {hasCoverPhoto ? (
            <img
              className="w-full h-full object-cover"
              src={coverPhotoURL}
              alt="band cover"
            />
          ) : (
            <CardImage className="w-full h-full text-gray-500 block" />
          )}
        </div>
        <GreenOutlineButton
          disabled={!btnActive}
          className="mt-2 w-full"
          onClick={handleClick}
        >
          {hasCoverPhoto ? "Replace Image" : "Upload an Image"}
        </GreenOutlineButton>
        <input
          type="file"
          ref={hiddenFileInput}
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) =>
            e.target.files?.[0] && onUploadCoverPhoto(e.target.files[0])
          }
        />
        {hasCoverPhoto ? (
          <div className="mt-2 text-center">
            <button
              className="text-sm text-red-700"
              disabled={!btnActive}
              style={{ cursor: "pointer" }}
              onClick={onRemovePhoto}
            >
              Remove Photo
            </button>
          </div>
        ) : null}
        {uploadError && (
          <div className="col-span-12 mt-2 text-red-600 text-sm">{uploadError}</div>
        )}
      </div>
      {uploadedFilePath && uploadedFilePath.length ? (
        <div className="mx-3 col-span-12 lg:col-span-3 w-full">
          <Cropper
            initialAspectRatio={16 / 9}
            aspectRatio={16 / 9}
            src={uploadedFilePath}
            viewMode={1}
            guides={true}
            minCropBoxHeight={10}
            minCropBoxWidth={10}
            background={false}
            responsive={true}
            autoCropArea={1}
            checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671
            onInitialized={(instance) => {
              setCropper(instance)
            }}
          />
          <div className="mt-2 mb-2 flex justify-between">
            <OutlineButton
              disabled={!btnActive}
              className="text-gray-700"
              onClick={onCancel}
            >
              Cancel
            </OutlineButton>
            <GreenOutlineButton disabled={!btnActive} onClick={onSave}>
              Crop and Save
            </GreenOutlineButton>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export const handleUpdateBandData = async (
  bandId: number,
  bandUpdateData: Record<string, any>,
): Promise<UpdateBandResponse | undefined> => {
  try {
    let responseData = await mutationRequest({
      path: `/api/v1/bands/${bandId}`,
      options: { method: "PUT" },
      data: { band: bandUpdateData },
    })
    return responseData
  } catch (error) {
    console.log("Error updating band data: ", error)
  }
}

export default BandEditPage
